// /groups/find — search public groups, and ask to join one.
//
// Signed in only, like the endpoint behind it. Discovery deliberately lives
// inside the app: there is no public page for a group and nothing crawlable.
//
// ⚠ WHAT A RESULT CARRIES IS DECIDED BY THE SERVER, not trimmed here.
// `PublicGroupSummary` is a hand-written allow-list -- no member names, no
// events, no roles -- so this view can render everything it is given without
// having to know what would be a leak. Do not fetch anything else about a group
// from this screen; a non-member is refused every other group read anyway.

import { ApiClientError, joinGroup, searchGroups } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { formatMemberCount, type PublicGroupSummary } from '@msc/shared';
import { formatEstablished } from './groupInfoPanel.js';

export const groupSearchView: View = async (outlet) => {
  const main = el('main', { class: 'page narrow' });

  const input = el('input', {
    id: 'q',
    type: 'search',
    name: 'q',
    placeholder: 'Poker, hiking, book club…',
    autocorrect: 'off',
    // ⚠ The STRING 'false', not the boolean. `el()` drops a `false` attribute
    // value entirely, which would render no attribute at all -- the opposite of
    // what is meant. See the CLAUDE.md row.
    spellcheck: 'false',
  }) as HTMLInputElement;

  const submit = el('button', { type: 'submit', class: 'btn' }, 'Search');
  const error = el('p', { class: 'form-error', hidden: true });
  const results = el('div', { class: 'gsearch-results' });

  // `method="post"` even though script submits it. Hard rule: if the script
  // ever fails, the browser must not fall back to GET and put the query in the
  // URL. It is only a search term here, but the rule has no exceptions.
  const form = el('form', { method: 'post', action: '/groups/find' }, [
    el('label', { for: 'q' }, 'Find a group'),
    input,
    submit,
    error,
  ]) as HTMLFormElement;

  let lastTerm = '';

  function renderResults(term: string, groups: PublicGroupSummary[]): void {
    if (!groups.length) {
      results.replaceChildren(
        el(
          'p',
          { class: 'muted' },
          `No public groups match “${term}”. Most groups are private and joined by invitation.`,
        ),
      );
      return;
    }
    results.replaceChildren(...groups.map(resultCard));
  }

  function resultCard(g: PublicGroupSummary): HTMLElement {
    const status = el('p', { class: 'muted small', hidden: true });
    const action = el('div', { class: 'gsearch-action' });

    function paintAction(standing: PublicGroupSummary['myStanding']): void {
      if (standing === 'member') {
        action.replaceChildren(
          el('a', { class: 'btn btn-sm', href: `/groups/${g.groupId}` }, 'Open'),
        );
        return;
      }
      if (standing === 'pending') {
        // Not a button. There is nothing left to press, and offering one would
        // just earn a 409 -- the server refuses a second pending request.
        action.replaceChildren(el('p', { class: 'muted small' }, 'Waiting for approval'));
        return;
      }
      const btn = el(
        'button',
        { type: 'button', class: 'btn btn-sm' },
        g.joinPolicy === 'instant' ? 'Join' : 'Ask to join',
      );
      btn.addEventListener('click', async () => {
        btn.setAttribute('disabled', 'disabled');
        status.hidden = true;
        try {
          const result = await joinGroup(g.groupId);
          // ⚠ Branch on the SERVER's outcome, never on g.joinPolicy. The policy
          // can change between this card being rendered and the press, and the
          // server reports what actually happened.
          if (result.outcome === 'joined') {
            navigate(`/groups/${result.groupId}`);
            return;
          }
          paintAction('pending');
          status.textContent = 'Asked to join. An owner or admin will decide.';
          status.hidden = false;
        } catch (err) {
          status.textContent =
            err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
          status.hidden = false;
          btn.removeAttribute('disabled');
        }
      });
      action.replaceChildren(btn);
    }
    paintAction(g.myStanding);

    const facts: HTMLElement[] = [
      el('span', {}, formatMemberCount(g.memberCount, g.memberNoun)),
    ];
    if (g.establishedOn) {
      facts.push(el('span', {}, `Since ${formatEstablished(g.establishedOn)}`));
    }

    return el('section', { class: 'card gsearch-card' }, [
      ...(g.photoUrl
        ? [
            el('div', { class: 'gsearch-photo' }, [
              el('img', { src: g.photoUrl, alt: g.name, loading: 'lazy' }),
            ]),
          ]
        : []),
      el('div', { class: 'gsearch-body' }, [
        el('h2', { class: 'gsearch-name' }, g.name),
        ...(g.about ? [el('p', {}, g.about)] : []),
        el('p', { class: 'muted small gsearch-facts' }, facts),
        status,
      ]),
      action,
    ]);
  }

  async function run(term: string): Promise<void> {
    lastTerm = term;
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    results.replaceChildren(el('p', { class: 'muted' }, 'Searching…'));
    try {
      const groups = await searchGroups(term);
      // Guard against an out-of-order response: a slower earlier search must
      // not overwrite the results of a later one.
      if (lastTerm !== term) return;
      renderResults(term, groups);
    } catch (err) {
      results.replaceChildren();
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Search failed. Try again.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  }

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const term = input.value.trim();
    if (term.length < 2) {
      // Matches the server, which answers a short term with [] rather than the
      // whole site. Said out loud here so an empty press is not silent.
      error.textContent = 'Type at least two characters.';
      error.hidden = false;
      results.replaceChildren();
      return;
    }
    void run(term);
  });

  main.replaceChildren(
    el('p', {}, [el('a', { href: '/groups' }, 'Back to your groups')]),
    el('h1', {}, 'Find a group'),
    el(
      'p',
      { class: 'muted' },
      'Only groups whose owner made them public appear here. Everything inside a ' +
        'group (its feed, events, chat and photos) stays members-only either way.',
    ),
    form,
    results,
  );
  outlet.replaceChildren(main);
};
